import {useState, useEffect} from "react"
import SimpleCoordinate from "./SimpleCoordinate"
import LandController from "./LandController"

export interface UseBoundaryResult {
    coordinates: SimpleCoordinate[]
    loading: boolean
    error: boolean
}

/**
 * Hook for fetching boundary coordinates for a land.
 * Handles loading state, errors, and cleanup.
 *
 * @param landId - The land ID to fetch boundaries for (undefined/null skips fetch)
 * @returns Object with coordinates array, loading state, and error state
 */
export function useBoundary(landId: number | undefined | null): UseBoundaryResult {
    const [coordinates, setCoordinates] = useState<SimpleCoordinate[]>([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(false)

    useEffect(() => {
        // Reset state when landId changes
        if (!landId) {
            setCoordinates([])
            setLoading(false)
            setError(false)
            return
        }

        let cancelled = false
        setLoading(true)
        setError(false)

        LandController.boundary(landId)
            .then(coords => {
                if (!cancelled) {
                    setCoordinates(coords)
                    setLoading(false)
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setCoordinates([])
                    setError(true)
                    setLoading(false)
                }
            })

        return () => {
            cancelled = true
        }
    }, [landId])

    return {coordinates, loading, error}
}
